import React, {useEffect} from "react";
import {useSelector} from "react-redux";
import styled from "styled-components";
import {Tooltip} from "antd";
import {useSubscription} from "../subscription/useSubscription";
import {acurisBrandColours, ellipsisStyle} from "../../style/styleConsts";
import {Cell, Table, Td, Th, Tr} from "../elements/Tables";
import PageSection from "../elements/PageSection";
import PageHeading from "../elements/PageHeading";
import {performancePeriodLabelForActivePeriod} from "../performance/period";
import {kpiBriefDescription} from "../performance/Kpi";
import PerformanceValue from "../performance/PerformanceValue";
import {NegativeValue, PositiveValue} from "../performance/StyledValue";
import TableContainer from "../elements/TableContainer";
import LoggingLink from "../elements/LoggingLink";
import {log} from "../../logging/logEvent";
import {CentredLoader} from "../elements/Loader";
import {asLocalTime} from "../../util/timeDescription";
import {Clock} from "js-joda";

const StyledLoggingLink = styled(LoggingLink)`
  color: inherit;
  text-decoration: none;
  display: block;
`;

const PortfolioLink = ({id, name, directToCompany, children}) => {
    const companyId = useSelector(state => state.userIdentity.companyId);

    const link = directToCompany
        ? `/our-programme/portfolio/${id}/company/${companyId}`
        : `/our-programme/portfolio/${id}`;

    return (
        <StyledLoggingLink
            to={link}
            loggingPayload={{type: `PORTFOLIO_ROW_CLICKED`, portfolioId: id, portfolioName: name}}
            className="t-portfolio-health-link">
            {children}
        </StyledLoggingLink>
    );
};

const Drilldown = ({portfolio, children}) => {
    if (portfolio.ranking) {
        return (
            <PortfolioLink id={portfolio.id}
                           name={portfolio.name}
                           directToCompany={!portfolio.owns}>
                {children}
            </PortfolioLink>
        );
    } else {
        return children;
    }
};

const PortfolioRowTr = styled(Tr)`
    ${props => (props.portfolio && !props.portfolio.ranking)
               ? ` opacity: 0.5;
                   cursor: not-allowed;
               `
               : ""
    }
`;

const PortfolioListTable = styled(Table)`
    ${PortfolioRowTr}:hover ${PositiveValue},
    ${PortfolioRowTr}:hover ${NegativeValue} {
        color: white;
    }
`;

const OptionalTooltip = ({title, mouseLeaveDelay = 0, mouseEnterDelay = 0.5, children}) => {
    if (!title)
        return children;

    return (
        <Tooltip title={title} mouseLeaveDelay={mouseLeaveDelay} mouseEnterDelay={mouseEnterDelay}>
            {children}
        </Tooltip>
    );
};

const PortfolioRow = ({portfolio, children}) => {
    const hasRanking = portfolio.ranking;

    return (
        <OptionalTooltip title={!hasRanking ? "No primary ranking is configured for this portfolio." : null}>
            <PortfolioRowTr portfolio={portfolio} clickable={hasRanking}>
                {React.Children.map(children, child =>
                    child ? (
                        <Td colSpan={child.props.colSpan ? child.props.colSpan : "initial"}>
                            <Drilldown portfolio={portfolio}>{child}</Drilldown>
                        </Td>
                    ) : null
                )}
            </PortfolioRowTr>
        </OptionalTooltip>
    );
};

export const PortfolioAsClickableRow = ({portfolio}) => {
    const clock = useSelector(({clock}) => clock || Clock.systemDefaultZone());

    if (portfolio.ranking) {
        return (
            <PortfolioRow portfolio={portfolio}>
                <LongStringEllipsedTextBox className="t-portfolio">{portfolio.name}</LongStringEllipsedTextBox>
                <LongStringEllipsedTextBox
                    className="t-ownership">{portfolio.internal ? "internal" : portfolio.owns ? null : portfolio.companyName}</LongStringEllipsedTextBox>
                <Cell className="t-connected-authors" right>{portfolio.numberOfConnectedAuthors}</Cell>
                <Cell className="t-ranked-authors"
                      right>{portfolio.performance.numberOfRankedAuthors}</Cell>
                <Cell className="t-new-ideas"
                      right>{portfolio.performance.numberOfNewIdeas}</Cell>
                <PerformancePeriod>{performancePeriodLabelForActivePeriod(portfolio.performance.period).toLowerCase()}</PerformancePeriod>
                <Cell className="t-kpi-value" right><PerformanceValue name={portfolio.performance.kpi}
                                                                      value={portfolio.performance.kpiValue}/></Cell>
                <Cell className="t-kpi">
                    {kpiBriefDescription(portfolio.performance.kpi, portfolio.currency)}
                </Cell>
                <Cell className="t-performance-timestamp" extraSubdued={true}>
                    <PerformanceTimestampText>as at {asLocalTime(portfolio.performance.generatedAt, clock)}</PerformanceTimestampText>
                </Cell>
            </PortfolioRow>
        );
    }
    else {
        return (
            <PortfolioRow portfolio={portfolio}>
                <LongStringEllipsedTextBox className="t-portfolio">{portfolio.name}</LongStringEllipsedTextBox>
                <LongStringEllipsedTextBox
                    className="t-ownership">{portfolio.internal ? "internal" : portfolio.owns ? null : portfolio.companyName}</LongStringEllipsedTextBox>
                <Cell className="t-connected-authors" right>{portfolio.numberOfConnectedAuthors}</Cell>
                <Cell className="t-ranked-authors" right/>
                <Cell className="t-new-ideas" right/>
                <PerformancePeriod/>
                <Cell className="t-kpi-value" right/>
                <Cell className="t-kpi">No Ranking</Cell>
                <Cell className="t-performance-timestamp"/>
            </PortfolioRow>
        );
    }
};

const PortfoliosTitle = styled(PageHeading)`
    padding-top: 30px;
    padding-bottom: 30px;
`;

const PortfoliosTableContainer = styled(TableContainer)`
    margin-bottom: 12px;
`;

const OurProgrammePage = ({}) => {
    useSubscription("PORTFOLIOS", "/ui/api/portfolios", 60);
    const portfolios = useSelector(state => state.portfolios.portfolios);
    useEffect(() => {
        log("PageComponentMounted", {page: "OurProgrammePage"});
        return () => {};
    }, []);

    if (!portfolios) {
        return <div style={{padding: 20}}><CentredLoader /></div>;
    }

    return (
        <PageSection>
            <PortfoliosTitle>Portfolios</PortfoliosTitle>
            <PortfoliosTableContainer>
                <PortfolioListTable className="t-portfolios">
                    <thead>
                    <tr>
                        <Th>Name</Th>
                        <Th/>
                        <Th><Cell right>Connected<br/> Authors</Cell></Th>
                        <Th><Cell right>Ranked<br/> Authors</Cell></Th>
                        <Th><Cell right>New<br/> Ideas</Cell></Th>
                        <Th/>
                        <Th><Cell right>KPI</Cell></Th>
                        <Th/>
                        <Th/>
                    </tr>
                    </thead>
                    <tbody>
                    {portfolios.map(portfolio => (
                        <PortfolioAsClickableRow portfolio={portfolio} key={portfolio.id}/>
                    ))}
                    </tbody>
                </PortfolioListTable>
            </PortfoliosTableContainer>
        </PageSection>
    );
};

export default OurProgrammePage;

const LongStringEllipsedTextBox = styled.div`
    ${ellipsisStyle};
    max-width: 300px;
`;

export const PerformancePeriod = styled.div`
  min-width: 90px;
`;

const PerformanceTimestampText = styled.span`
  font-size: 10px;
`;
